import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { supabase, Review } from '../../lib/supabase';

export default function ReviewsPage() {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    author: '',
    email: '',
    rating: 5,
    content: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  useEffect(() => {
    fetchReviews();
    
    // SEO Meta Tags
    document.title = '고객 후기 - 유흥디자인랩 | 실제 이용 고객 리뷰';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '유흥디자인랩을 이용한 고객들의 생생한 후기를 확인하세요. 구인광고 및 홈페이지 제작 서비스에 대한 실제 이용 경험과 평가를 만나보세요.');
    }
  }, []);

  useEffect(() => {
    if (reviews.length > 0) {
      // Schema.org JSON-LD for Reviews
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "유흥디자인랩",
        "url": import.meta.env.VITE_SITE_URL || "https://example.com",
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1),
          "reviewCount": reviews.length,
          "bestRating": "5",
          "worstRating": "1"
        },
        "review": reviews.slice(0, 10).map(review => ({
          "@type": "Review",
          "author": {
            "@type": "Person",
            "name": review.author
          },
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": review.rating,
            "bestRating": "5",
            "worstRating": "1"
          },
          "reviewBody": review.content
        }))
      });
      document.head.appendChild(script);

      return () => {
        document.head.removeChild(script);
      };
    }
  }, [reviews]);

  const fetchReviews = async () => {
    try {
      const { data, error } = await supabase
        .from('reviews')
        .select('*')
        .eq('approved', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setReviews(data || []);
    } catch (error) {
      console.error('후기 로딩 오류:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitMessage('');

    try {
      const { error } = await supabase.from('reviews').insert([
        {
          author: formData.author,
          email: formData.email,
          rating: formData.rating,
          content: formData.content,
          approved: false,
        },
      ]);

      if (error) throw error;

      setSubmitMessage('후기가 성공적으로 제출되었습니다! 관리자 승인 후 게시됩니다.');
      setFormData({ author: '', email: '', rating: 5, content: '' });
      setTimeout(() => {
        setShowForm(false);
        setSubmitMessage('');
      }, 3000);
    } catch (error) {
      console.error('후기 제출 오류:', error);
      setSubmitMessage('후기 제출 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <i
        key={i}
        className={`ri-star-${i < rating ? 'fill' : 'line'} text-yellow-400`}
      ></i>
    ));
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <section className="pt-32 pb-16 bg-gradient-to-br from-neon-pink/5 to-purple-500/5">
        <div className="max-w-7xl mx-auto px-6">
          <h1 className="text-5xl font-bold mb-6 text-center">고객 후기</h1>
          <p className="text-xl text-gray-600 text-center max-w-3xl mx-auto mb-8">
            함께한 고객들의 생생한 경험을 들어보세요
          </p>
          <div className="text-center">
            <button
              onClick={() => setShowForm(true)}
              className="bg-neon-pink text-white px-8 py-3 rounded-full font-semibold hover:bg-neon-pink/90 transition-colors cursor-pointer whitespace-nowrap"
            >
              후기 작성하기
            </button>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          {loading ? (
            <div className="text-center py-20">
              <p className="text-gray-600 text-lg">로딩 중...</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {reviews.map((review) => (
                <div
                  key={review.id}
                  className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex gap-1 mb-4">{renderStars(review.rating)}</div>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    {review.content}
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-neon-pink to-purple-500 rounded-full flex items-center justify-center text-white font-bold">
                      {review.author.charAt(0)}
                    </div>
                    <div>
                      <p className="font-semibold">{review.author}</p>
                      <p className="text-sm text-gray-500">고객</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && reviews.length === 0 && (
            <div className="text-center py-20">
              <p className="text-gray-600 text-lg">아직 등록된 후기가 없습니다.</p>
            </div>
          )}
        </div>
      </section>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">후기 작성하기</h2>
              <button
                onClick={() => setShowForm(false)}
                className="text-gray-500 hover:text-gray-700 cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold mb-2">
                  이름 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.author}
                  onChange={(e) =>
                    setFormData({ ...formData, author: e.target.value })
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neon-pink text-sm"
                  placeholder="이름을 입력하세요"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">
                  이메일 <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neon-pink text-sm"
                  placeholder="이메일을 입력하세요"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">
                  평점 <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setFormData({ ...formData, rating: star })}
                      className="cursor-pointer"
                    >
                      <i
                        className={`ri-star-${
                          star <= formData.rating ? 'fill' : 'line'
                        } text-3xl text-yellow-400`}
                      ></i>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">
                  후기 내용 <span className="text-red-500">*</span>
                </label>
                <textarea
                  required
                  value={formData.content}
                  onChange={(e) => {
                    if (e.target.value.length <= 500) {
                      setFormData({ ...formData, content: e.target.value });
                    }
                  }}
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neon-pink text-sm resize-none"
                  placeholder="서비스 이용 경험을 자세히 작성해주세요 (최대 500자)"
                />
                <p className="text-sm text-gray-500 mt-1">
                  {formData.content.length}/500자
                </p>
              </div>

              {submitMessage && (
                <div
                  className={`p-4 rounded-lg ${
                    submitMessage.includes('성공')
                      ? 'bg-green-50 text-green-700'
                      : 'bg-red-50 text-red-700'
                  }`}
                >
                  {submitMessage}
                </div>
              )}

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="flex-1 px-6 py-3 border border-gray-300 rounded-lg font-semibold hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  취소
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-neon-pink text-white px-6 py-3 rounded-lg font-semibold hover:bg-neon-pink/90 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  {submitting ? '제출 중...' : '제출하기'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
